import { Stack } from '@/components/layout';
import { Avatar } from '@/components/ui';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { memo, type ComponentProps } from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

export type GuardianKidCardAvatarSource = ComponentProps<typeof Avatar.Image>['source'];

export type GuardianKidCardAvatarProps = {
  color: string;
  progress: number;
  source: GuardianKidCardAvatarSource;
  hasAvatar: boolean;
};

export const GuardianKidCardAvatar = memo(
  ({ color, progress, source, hasAvatar }: GuardianKidCardAvatarProps) => {
    const { scale } = useScaling();

    const RING_SIZE = scale(60);
    const RING_STROKE_WIDTH = scale(4);
    const AVATAR_SIZE = scale(48);

    const radius = RING_SIZE / 2 - RING_STROKE_WIDTH / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - progress);

    return (
      <Stack width={RING_SIZE} height={RING_SIZE} align="center" justify="center">
        <Svg width={RING_SIZE} height={RING_SIZE} style={StyleSheet.absoluteFill}>
          <Circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            stroke={color['0']}
            strokeWidth={RING_STROKE_WIDTH}
            fill="none"
          />
          <Circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            stroke={color}
            strokeWidth={RING_STROKE_WIDTH}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        </Svg>

        <Avatar size={AVATAR_SIZE} borderWidth={0} activeOpacity={1}>
          <Avatar.Image
            source={source}
            size={hasAvatar ? undefined : AVATAR_SIZE}
            disableCircular={!hasAvatar}
            backgroundColor={colors['1']}
          />
        </Avatar>
      </Stack>
    );
  },
);

export default GuardianKidCardAvatar;
